import { supabase } from '../lib/supabase';

export const ExportAndSendService = {
  async getExportsByProjectId(projectId) {
    try {
      const { data, error } = await supabase
        .from('export_and_send')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Debug: Log what we got from database
      console.log(`📅 [getExportsByProjectId] Fetched ${data?.length || 0} exports for project ${projectId}`);
      console.log(`📅 [getExportsByProjectId] RAW DATA:`, JSON.stringify(data, null, 2));
      if (data && data.length > 0) {
        data.forEach((exp, idx) => {
          console.log(`📅 [getExportsByProjectId] Export ${idx + 1}:`, {
            id: exp.id,
            export_date: exp.export_date,
            has_export_date: 'export_date' in exp,
            created_at: exp.created_at,
            status: exp.status,
            allKeys: Object.keys(exp).join(', ')
          });
        });
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching exports:', error);
      throw error;
    }
  },

  async createExport(exportData) {
    try {
      const { data, error } = await supabase
        .from('export_and_send')
        .insert([exportData])
        .select('*')  // Get full record, not just ID
        .single();

      if (error) {
        console.error('[ExportAndSendService] Error creating export:', error);
        throw error;
      }

      if (!data || !data.id) {
        throw new Error('Export created but no data returned');
      }
      // Verify the record actually exists in the database
      const { data: verifyData, error: verifyError } = await supabase
        .from('export_and_send')
        .select('id, project_id, status')
        .eq('id', data.id)
        .single();

      if (verifyError || !verifyData) {
        console.error('[ExportAndSendService] ❌ Export verification failed!', verifyError);
        throw new Error('Export created but cannot be verified in database');
      }
      // Return the full created record
      return data;
    } catch (error) {
      console.error('[ExportAndSendService] Error in createExport:', error);
      throw error;
    }
  },

  async updateExport(id, updates) {
    try {
      // Ensure id is a valid UUID string
      if (!id || typeof id !== 'string') {
        throw new Error(`Invalid export ID: ${id}`);
      }
      const numericId = id; // Keep as UUID string

      // Enhanced logging for export_date specifically
      console.log('📅 [ExportService] UPDATE CALLED with these keys:', Object.keys(updates));
      console.log('📅 [ExportService] export_date value:', updates.export_date, 'type:', typeof updates.export_date);

      // Log ALL fields being updated (truncated for readability)
      const logUpdate = { ...updates };
      Object.keys(logUpdate).forEach(key => {
        if (key.includes('files') && Array.isArray(logUpdate[key])) {
          logUpdate[key] = `[${logUpdate[key].length} files]`;
        }
      });
      console.log('📅 [ExportService] Full update payload:', logUpdate);

      const { data, error } = await supabase
        .from('export_and_send')
        .update(updates)
        .eq('id', numericId)
        .select('id, export_date, updated_at');

      if (error) {
        console.error('❌ [ExportService] Update failed:', error);
        throw error;
      }

      // Verify the update worked
      if (data && data[0] && updates.export_date) {
        console.log('✅ [ExportService] Export updated, verified export_date:', data[0].export_date);
      }

      // Return minimal success response with the updates applied
      return { id, ...updates, success: true };
    } catch (error) {
      console.error('Error updating export:', error);
      throw error;
    }
  },

  async deleteExport(id) {
    try {
      // Ensure id is a valid UUID string
      if (!id || typeof id !== 'string') {
        throw new Error(`Invalid export ID: ${id}`);
      }
      const numericId = id; // Keep as UUID string

      const { error } = await supabase
        .from('export_and_send')
        .delete()
        .eq('id', numericId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting export:', error);
      throw error;
    }
  },

  async sendExportEmail(exportId, emailBody, emailTo = [], emailCc = [], emailSubject = "", sentBy = null, downloadLink = null) {
    try {
      // Validate payload sizes to prevent "payload string too long" errors
      const maxTextSize = 64000; // ~64KB limit for text fields
      const maxSubjectSize = 255; // Standard email subject limit
      
      if (emailBody && emailBody.length > maxTextSize) {
        throw new Error(`Email content is too large (${emailBody.length} characters). Maximum allowed is ${maxTextSize} characters.`);
      }
      
      if (emailSubject && emailSubject.length > maxSubjectSize) {
        throw new Error(`Email subject is too long (${emailSubject.length} characters). Maximum allowed is ${maxSubjectSize} characters.`);
      }
      // Ensure exportId is a valid UUID string
      if (!exportId || typeof exportId !== 'string') {
        throw new Error(`Invalid export ID: ${exportId}`);
      }
      const numericExportId = exportId; // Keep as UUID string

      // First set send_email to false to reset the trigger
      const { error: resetError } = await supabase
        .from('export_and_send')
        .update({
          send_email: false,
        })
        .eq('id', numericExportId);

      if (resetError) {
        console.error('Error resetting send_email:', resetError);
        throw resetError;
      }
      // Then set it to true along with other updates to trigger the n8n workflow
      const updateData = {
        email_body: emailBody,
        email_to: emailTo,
        email_cc: emailCc,
        email_subject: emailSubject,
        sent_date: new Date().toISOString(),
        status: 'sent',
        send_email: true,
        updated_at: new Date().toISOString(),
        sent_by: sentBy,
      };
      
      // Include download_link if provided
      if (downloadLink) {
        updateData.download_link = downloadLink;
      }
      const { error } = await supabase
        .from('export_and_send')
        .update(updateData)
        .eq('id', numericExportId);

      if (error) {
        console.error('Error updating export with send_email=true:', error);
        console.error('Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        throw error;
      }
      // Verify that the trigger created a queue entry
      const { data: queueEntry, error: queueError } = await supabase
        .from('email_notification_queue')
        .select('id, created_at, processed, metadata')
        .eq('export_id', numericExportId)
        .eq('processed', false)
        .order('created_at', { ascending: false })
        .limit(1);
        
      if (queueError) {
        console.warn('Warning: Could not verify queue entry:', queueError);
      } else if (!queueEntry || queueEntry.length === 0) {
        console.warn('⚠️ Queue entry not found yet - trigger may still be processing.');
        console.warn('send_email was set to true successfully, so the trigger should have fired.');
      } else {
      }
      // Return success response with queue information
      return { 
        success: true, 
        id: numericExportId,
        queue_entry_id: queueEntry?.[0]?.id || null 
      };
    } catch (error) {
      console.error('=== ERROR IN EXPORT EMAIL SEND PROCESS ===');
      console.error('Error sending export email:', error);
      console.error('Full error object:', JSON.stringify(error, null, 2));
      throw error;
    }
  },

  // Workaround method that splits the update to minimize trigger payload
  async sendExportEmailWorkaround(exportId, emailBody, emailTo = [], emailCc = [], emailSubject = "", sentBy = null, downloadLink = null) {
    try {
      // Validate payload sizes to prevent "payload string too long" errors
      const maxTextSize = 64000; // ~64KB limit for text fields
      const maxSubjectSize = 255; // Standard email subject limit

      if (emailBody && emailBody.length > maxTextSize) {
        throw new Error(`Email content is too large (${emailBody.length} characters). Maximum allowed is ${maxTextSize} characters.`);
      }

      if (emailSubject && emailSubject.length > maxSubjectSize) {
        throw new Error(`Email subject is too long (${emailSubject.length} characters). Maximum allowed is ${maxSubjectSize} characters.`);
      }

      console.log('=== EXPORT EMAIL SEND (QUEUE METHOD) ===');
      // Ensure exportId is a valid UUID string
      if (!exportId || typeof exportId !== 'string') {
        throw new Error(`Invalid export ID: ${exportId}`);
      }
      const numericExportId = exportId; // Keep as UUID string

      // CRITICAL: First set send_email to false to reset the trigger
      const { error: resetError } = await supabase
        .from('export_and_send')
        .update({
          send_email: false,
        })
        .eq('id', numericExportId)
        .select();
      if (resetError) {
        console.error('Error resetting send_email:', resetError);
        throw resetError;
      }
      // Wait a moment to ensure the database processes the first update
      await new Promise(resolve => setTimeout(resolve, 100));

      // Then update all email fields and set send_email to true to trigger the queue
      const updateData = {
        email_body: emailBody,
        email_to: emailTo,
        email_cc: emailCc,
        email_subject: emailSubject,
        sent_date: new Date().toISOString(),
        status: 'sent',
        updated_at: new Date().toISOString(),
        send_email: true, // This triggers the queue insertion
        sent_by: sentBy
      };

      // Include download_link if provided
      if (downloadLink) {
        updateData.download_link = downloadLink;
      }
      const { error } = await supabase
        .from('export_and_send')
        .update(updateData)
        .eq('id', numericExportId)
        .select();
      if (error) {
        console.error('Error updating export:', error);
        throw error;
      }
      // Wait a bit for the trigger to fire and create queue entry
      await new Promise(resolve => setTimeout(resolve, 500));

      // Verify that the trigger created a queue entry
      const { data: queueEntry, error: queueError } = await supabase
        .from('email_notification_queue')
        .select('id, created_at, processed, metadata')
        .eq('export_id', numericExportId)
        .eq('processed', false)
        .order('created_at', { ascending: false })
        .limit(1);

      if (queueError) {
        console.warn('⚠️ Warning: Could not verify queue entry:', queueError);
        console.warn('This might mean the queue table does not exist.');
      } else if (!queueEntry || queueEntry.length === 0) {
        // Queue entry may not be visible yet due to timing - log warning but don't throw.
        // The trigger has already fired at this point; throwing here previously caused
        // the fallback in ExportForm to re-send, creating duplicate drafts.
        console.warn('⚠️ Queue entry not found yet - trigger may still be processing.');
        console.warn('send_email was set to true successfully, so the trigger should have fired.');
      } else {
      }
      // Return success response with queue information
      return { 
        success: true, 
        id: numericExportId,
        queue_entry_id: queueEntry?.[0]?.id || null 
      };
    } catch (error) {
      console.error('=== ERROR IN EXPORT EMAIL QUEUE ===');
      console.error('Error in queue method:', error);
      throw error;
    }
  },

  // New method to mark queue notifications as processed (for n8n to call)
  async markNotificationProcessed(notificationId, error = null) {
    try {
      const { data, error: updateError } = await supabase
        .from('email_notification_queue')
        .update({
          processed: true,
          processed_at: new Date().toISOString(),
          error_message: error
        })
        .eq('id', notificationId)
        .select()
        .single();

      if (updateError) throw updateError;
      return data;
    } catch (error) {
      console.error('Error marking notification as processed:', error);
      throw error;
    }
  },

  // Method to get unprocessed notifications (for n8n to poll)
  async getUnprocessedNotifications(limit = 10) {
    try {
      const { data, error } = await supabase
        .from('email_notification_queue')
        .select('*')
        .eq('processed', false)
        .order('created_at', { ascending: true })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching unprocessed notifications:', error);
      throw error;
    }
  }
};